import React, { useState, ReactNode, useEffect } from 'react';
import {
  AppBar as MuiAppBar,
  Box,
  Drawer as MuiDrawer,
  IconButton,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as SurveysIcon,
  Nature as PlantsIcon,
  Assessment as ReportsIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  PostAdd as CreateSurveyIcon,
  MoreVert as MoreVertIcon,
  Agriculture as FarmersIcon,
  People as ParticipantsIcon,
  BugReport as DiseasePestsIcon,
  CloudUpload as ImportIcon,
  Storage as DataIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use i18n.dir() directly for real-time direction detection
  const isRtl = i18n.dir() === 'rtl';

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [forceUpdate, setForceUpdate] = useState(0);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const menuItems = [
    { key: 'dashboard', label: t('dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { key: 'createSurvey', label: t('createSurvey'), icon: <CreateSurveyIcon />, path: '/survey/new' },
    { key: 'surveys', label: t('surveys'), icon: <SurveysIcon />, path: '/surveys' },
    { key: 'importSurveys', label: t('importSurveys'), icon: <ImportIcon />, path: '/import-surveys' },
    { key: 'referenceData', label: t('referenceData'), icon: <DataIcon />, path: '/reference-data' },
    { key: 'farmers', label: t('farmers'), icon: <FarmersIcon />, path: '/farmers' },
    { key: 'participants', label: t('participants'), icon: <ParticipantsIcon />, path: '/participants' },
    { key: 'diseasePests', label: t('diseasesAndPests'), icon: <DiseasePestsIcon />, path: '/disease-pests' },
    { key: 'seedReports', label: t('seedReports'), icon: <AssessmentIcon />, path: '/seed-reports' },
    { key: 'reports', label: t('reports'), icon: <ReportsIcon />, path: '/reports' },
  ];

  const drawerContent = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #1e293b 0%, #334155 100%)',
      color: 'white'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        p: 3, 
        textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Typography 
          variant="h5" 
          component="div" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(45deg, #60a5fa, #34d399)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          🌱 {t('appName')}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {t('seedManagement')}
        </Typography>
      </Box>

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {menuItems.map((item) => (
            <ListItem 
              key={item.path} 
              disablePadding 
              sx={{ mb: 1 }}
            >
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  px: 2,
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(96,165,250,0.2)',
                    color: '#60a5fa',
                    borderLeft: '3px solid #60a5fa',
                    '&:hover': {
                      bgcolor: 'rgba(96,165,250,0.3)',
                    },
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon sx={{ 
                  color: 'inherit', 
                  minWidth: 40,
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.3rem'
                  }
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  sx={{ 
                    '& .MuiTypography-root': {
                      fontWeight: 500,
                      fontSize: '0.95rem'
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* User Profile Section */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(0,0,0,0.2)'
      }}>
        {/* Language Selector */}
        <Box sx={{ mb: 2 }}>
          <Select
            fullWidth
            value={i18n.language}
            onChange={(e) => i18n.changeLanguage(e.target.value as string)}
            sx={{
              color: 'white',
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.5)',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            }}
          >
            <MenuItem value="ar">🇸🇦 العربية</MenuItem>
            <MenuItem value="en">🇺🇸 English</MenuItem>
          </Select>
        </Box>

        {/* User Profile */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.05)'
        }}>
          <Avatar sx={{ 
            bgcolor: '#60a5fa',
            width: 40,
            height: 40,
            fontSize: '1.1rem'
          }}>
            👤
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.username || t('user')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {t('administrator')}
            </Typography>
          </Box>
          <IconButton
            onClick={handleMenuOpen}
            sx={{ 
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  useEffect(() => {
    setForceUpdate((prev) => prev + 1);
  }, [i18n.language]);

  return (
    <Box sx={{ display: 'flex', width: '100vw', height: '100vh', margin: 0, padding: 0 }}>
      {/* Mobile Drawer */}
      <MuiDrawer
        variant="temporary"
        anchor={isRtl ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </MuiDrawer>

      {/* Desktop Drawer */}
      <MuiDrawer
        variant="permanent"
        anchor={isRtl ? 'right' : 'left'}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            position: 'relative',
            height: '100vh',
          },
        }}
      >
        {drawerContent}
      </MuiDrawer>

      {/* Main Content Area */}
      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}>
        {/* AppBar */}
        <MuiAppBar 
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e5e7eb',
            color: '#1e293b',
          }}
        >
          <Toolbar sx={{ 
            justifyContent: 'space-between',
            px: 3,
            py: 1
          }}>
            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                display: { md: 'none' },
                color: '#64748b'
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Page Title */}
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                flexGrow: 1,
                fontWeight: 600,
                color: '#1e293b',
                display: { xs: 'none', md: 'block' }
              }}
            >
              {/* You can add dynamic page title here */}
            </Typography>

            {/* Right side controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Language Selector */}
              <Select
                value={i18n.language}
                onChange={(e) => {
                  i18n.changeLanguage(e.target.value);
                  setForceUpdate(prev => prev + 1);
                }}
                size="small"
                sx={{
                  minWidth: 100,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #d1d5db'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#9ca3af'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#60a5fa'
                  }
                }}
              >
                <MenuItem value="ar">{t('arabic')}</MenuItem>
                <MenuItem value="en">{t('english')}</MenuItem>
              </Select>

              {/* Notifications */}
              <IconButton 
                size="large"
                sx={{
                  color: '#64748b',
                  position: 'relative',
                  '&:hover': {
                    bgcolor: 'rgba(100, 116, 139, 0.1)'
                  }
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  🔔
                  <Box sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 8,
                    height: 8,
                    bgcolor: '#ef4444',
                    borderRadius: '50%',
                  }} />
                </Box>
              </IconButton>

              {/* User Avatar */}
              <IconButton 
                size="large" 
                onClick={handleMenuOpen}
                sx={{
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(100, 116, 139, 0.1)'
                  }
                }}
              >
                <Avatar sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: '#60a5fa',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}>
                  {user?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>

          {/* User Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: 2,
                minWidth: 200,
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                mt: 1
              }
            }}
          >
            {/* User Info */}
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {user?.email}
              </Typography>
            </Box>

            {/* Menu Items */}
            <MenuItem 
              onClick={() => { handleMenuClose(); navigate('/profile'); }}
              sx={{
                py: 1.5,
                px: 3,
                '&:hover': {
                  bgcolor: 'rgba(96, 165, 250, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#64748b' }}>
                👤
              </ListItemIcon>
              <Typography sx={{ color: '#1e293b' }}>
                {t('profile')}
              </Typography>
            </MenuItem>

            <MenuItem 
              onClick={() => { handleMenuClose(); navigate('/settings'); }}
              sx={{
                py: 1.5,
                px: 3,
                '&:hover': {
                  bgcolor: 'rgba(96, 165, 250, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#64748b' }}>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <Typography sx={{ color: '#1e293b' }}>
                {t('settings')}
              </Typography>
            </MenuItem>

            <Divider sx={{ my: 1 }} />

            <MenuItem 
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 3,
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#ef4444' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <Typography sx={{ color: '#ef4444' }}>
                {t('logout')}
              </Typography>
            </MenuItem>
          </Menu>
        </MuiAppBar>

        {/* Main content */}
        <Box sx={{ 
          flexGrow: 1,
          overflow: 'auto',
          p: 3
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
